import React from "react";
import Loading from "../../components/common/loading";
import axios from "axios";
import NotificationManager from "../../utils/notificationManager";

const styles = {
  pageWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  headerCard: {
    background: "transparent",
    borderRadius: 0,
    border: "none",
    boxShadow: "none",
    padding: 0,
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    justifyContent: "space-between",
  },
  headerTopRow: {
    display: "flex",
    flex: "1 1 360px",
    flexDirection: "column",
    gap: "12px",
  },
  title: {
    margin: 0,
    fontSize: "22px",
    fontWeight: 600,
    color: "#1f2d3d",
  },
  subtitle: {
    margin: 0,
    color: "#607284",
    fontSize: "14px",
    lineHeight: 1.7,
  },
  statusBadge: (enabled) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: 500,
    background: enabled ? "rgba(34,197,94,0.12)" : "rgba(226,232,240,0.65)",
    color: enabled ? "#166534" : "#475569",
    border: `1px solid ${enabled ? "rgba(34,197,94,0.3)" : "rgba(203,213,225,0.9)"}`,
  }),
  statusIcon: {
    fontSize: "18px",
  },
  statusHint: {
    margin: 0,
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  headerActions: {
    display: "flex",
    flex: "0 0 auto",
    justifyContent: "flex-end",
    gap: "12px",
    flexWrap: "wrap",
  },
  actionButton: {
    minWidth: "110px",
    padding: "10px 16px",
    borderRadius: "3px",
    border: "none",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  secondaryButton: {
    background: "#ffffff",
    border: "1px solid #cbd5f5",
    color: "#1f2d3d",
  },
  primaryButton: {
    background: "var(--highlight-color)",
    border: "1px solid var(--highlight-color)",
    color: "var(--highlight-font-color)",
  },
  disabledButton: {
    opacity: 0.5,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "24px",
  },
  protocolSelector: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "8px",
  },
  protocolButton: (active) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    padding: "12px 14px",
    borderRadius: "5px",
    border: `1px solid ${active ? "var(--highlight-color)" : "#d8e3f8"}`,
    background: active ? "rgba(37, 99, 235, 0.08)" : "#ffffff",
    color: active ? "#1d4ed8" : "#334155",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.2s ease",
  }),
  protocolLabel: {
    fontSize: "14px",
    fontWeight: 700,
  },
  protocolDescription: {
    fontSize: "12px",
    lineHeight: 1.45,
    color: "#64748b",
  },
  infoCard: {
    background: "#ffffff",
    borderRadius: 4,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(15,23,42,0.06)",
    padding: "24px 28px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  settingsCard: {
    background: "#ffffff",
    borderRadius: 4,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
    padding: "24px 28px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  cardTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 600,
    color: "#1f2d3d",
  },
  cardDescription: {
    margin: 0,
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.6,
  },
  guideBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    borderRadius: 4,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: "16px 18px",
  },
  guideTitle: {
    margin: 0,
    fontSize: "14px",
    fontWeight: 600,
    color: "#1f2d3d",
  },
  stepList: {
    margin: 0,
    paddingLeft: "18px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: "#334155",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  inlineCode: {
    fontFamily: "SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace",
    background: "#eef2ff",
    color: "#312e81",
    padding: "2px 6px",
    borderRadius: "6px",
  },
  fieldGroup: {
    borderRadius: 5,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  readOnlyGroup: {
    borderRadius: 5,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  fieldHeading: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldTitle: {
    margin: 0,
    fontSize: "15px",
    fontWeight: 600,
    color: "#1f2d3d",
  },
  fieldDescription: {
    margin: 0,
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    borderRadius: "3px",
    border: "1px solid #cbd5f5",
    background: "#ffffff",
    fontSize: "14px",
    color: "#1f2d3d",
    outline: "none",
    boxShadow: "none",
    transition: "all 0.2s ease",
  },
  selectInput: {
    width: "100%",
    height: "44px",
    minHeight: "44px",
    padding: "0 36px 0 14px",
    borderRadius: "3px",
    border: "1px solid #cbd5f5",
    background: "#ffffff",
    fontSize: "14px",
    lineHeight: "normal",
    color: "#1f2d3d",
    outline: "none",
    boxShadow: "none",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
  },
  textarea: {
    width: "100%",
    minHeight: "160px",
    padding: "11px 14px",
    borderRadius: "10px",
    border: "1px solid #cbd5f5",
    background: "#ffffff",
    fontSize: "14px",
    color: "#1f2d3d",
    outline: "none",
    boxShadow: "none",
    lineHeight: 1.6,
    resize: "vertical",
  },
  helperText: {
    margin: 0,
    color: "#94a3b8",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  readOnlyField: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    background: "#f8fafc",
    borderRadius: "3px",
    padding: "12px 14px",
    border: "1px solid #d8e3f8",
  },
  codeValue: {
    flex: 1,
    fontFamily: "SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace",
    fontSize: "13px",
    color: "#0f172a",
    wordBreak: "break-all",
    whiteSpace: "pre-wrap",
  },
  copyButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #cbd5f5",
    background: "#ffffff",
    color: "#1d4ed8",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "13px",
    transition: "all 0.2s ease",
  },
  copyButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  copyIcon: {
    fontSize: "16px",
  },
  sectionDivider: {
    height: "1px",
    background: "#e2e8f0",
    margin: "8px 0",
  },
  toggleButton: (enabled) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "14px",
    borderRadius: "6px",
    padding: "10px 16px",
    border: `1px solid ${enabled ? "rgba(34,197,94,0.3)" : "#cbd5f5"}`,
    background: enabled ? "rgba(34,197,94,0.12)" : "#ffffff",
    color: enabled ? "#166534" : "#1f2d3d",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
  }),
  toggleIndicator: (enabled) => ({
    position: "relative",
    width: "44px",
    height: "22px",
    borderRadius: "999px",
    background: enabled ? "#22c55e" : "#cbd5f5",
    transition: "all 0.2s ease",
  }),
  toggleKnob: (enabled) => ({
    position: "absolute",
    top: "3px",
    left: enabled ? "24px" : "4px",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    background: "#ffffff",
    boxShadow: "0 2px 6px rgba(15,23,42,0.2)",
    transition: "all 0.2s ease",
  }),
};

const darkOverrides = {
  headerCard: {
    background: "transparent",
    border: "none",
    boxShadow: "none",
    borderRadius: 0,
  },
  title: {
    color: "#f8fafc",
  },
  subtitle: {
    color: "#cbd5e1",
  },
  statusBadge: (enabled) => ({
    ...styles.statusBadge(enabled),
    background: enabled ? "rgba(34,197,94,0.2)" : "rgba(148,163,184,0.18)",
    color: enabled ? "#bbf7d0" : "#cbd5e1",
    border: `1px solid ${enabled ? "rgba(34,197,94,0.4)" : "rgba(148,163,184,0.35)"}`,
  }),
  statusHint: {
    color: "#94a3b8",
  },
  secondaryButton: {
    background: "rgba(15, 23, 42, 0.65)",
    border: "1px solid rgba(148, 163, 184, 0.4)",
    color: "#f8fafc",
  },
  infoCard: {
    background: "rgba(15, 23, 42, 0.72)",
    border: "1px solid rgba(148, 163, 184, 0.25)",
    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.35)",
  },
  settingsCard: {
    background: "rgba(15, 23, 42, 0.78)",
    border: "1px solid rgba(148, 163, 184, 0.25)",
    boxShadow: "0 18px 35px rgba(15, 23, 42, 0.4)",
  },
  cardTitle: {
    color: "#f8fafc",
  },
  cardDescription: {
    color: "#94a3b8",
  },
  guideBlock: {
    background: "rgba(15, 23, 42, 0.65)",
    border: "1px solid rgba(148, 163, 184, 0.25)",
  },
  guideTitle: {
    color: "#f8fafc",
  },
  stepList: {
    color: "#cbd5e1",
  },
  inlineCode: {
    background: "rgba(79, 70, 229, 0.25)",
    color: "#c7d2fe",
  },
  fieldGroup: {
    background: "rgba(15, 23, 42, 0.65)",
    border: "1px solid rgba(148, 163, 184, 0.25)",
  },
  readOnlyGroup: {
    background: "rgba(15, 23, 42, 0.65)",
    border: "1px solid rgba(148, 163, 184, 0.25)",
  },
  fieldTitle: {
    color: "#f8fafc",
  },
  fieldDescription: {
    color: "#94a3b8",
  },
  input: {
    border: "1px solid rgba(148, 163, 184, 0.4)",
    background: "rgba(15, 23, 42, 0.55)",
    color: "#f8fafc",
  },
  selectInput: {
    border: "1px solid rgba(148, 163, 184, 0.4)",
    background: "rgba(15, 23, 42, 0.55)",
    color: "#f8fafc",
  },
  textarea: {
    border: "1px solid rgba(148, 163, 184, 0.4)",
    background: "rgba(15, 23, 42, 0.55)",
    color: "#f8fafc",
  },
  helperText: {
    color: "#94a3b8",
  },
  readOnlyField: {
    background: "rgba(15, 23, 42, 0.6)",
    border: "1px solid rgba(148, 163, 184, 0.35)",
  },
  codeValue: {
    color: "#e2e8f0",
  },
  copyButton: {
    background: "rgba(15, 23, 42, 0.65)",
    border: "1px solid rgba(147, 197, 253, 0.35)",
    color: "#93c5fd",
  },
  copyIcon: {
    color: "#93c5fd",
  },
  protocolButton: (active) => ({
    ...styles.protocolButton(active),
    background: active ? "rgba(96, 165, 250, 0.16)" : "rgba(15, 23, 42, 0.65)",
    border: `1px solid ${active ? "rgba(96, 165, 250, 0.5)" : "rgba(148, 163, 184, 0.35)"}`,
    color: active ? "#bfdbfe" : "#f8fafc",
  }),
  protocolDescription: {
    color: "#94a3b8",
  },
  sectionDivider: {
    background: "rgba(148, 163, 184, 0.35)",
  },
  toggleButton: (enabled) => ({
    ...styles.toggleButton(enabled),
    background: enabled ? "rgba(34,197,94,0.22)" : "rgba(15, 23, 42, 0.65)",
    border: `1px solid ${enabled ? "rgba(34,197,94,0.4)" : "rgba(148,163,184,0.3)"}`,
    color: enabled ? "#bbf7d0" : "#f8fafc",
  }),
  toggleIndicator: (enabled) => ({
    ...styles.toggleIndicator(enabled),
    background: enabled ? "#22c55e" : "rgba(148, 163, 184, 0.35)",
    boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.45)",
  }),
  toggleKnob: (enabled) => ({
    ...styles.toggleKnob(enabled),
    background: "#0f172a",
    boxShadow: "0 4px 8px rgba(15,23,42,0.45)",
  }),
};

const mergeStyles = (base, overrides) => {
  const merged = { ...base };
  Object.keys(overrides).forEach((key) => {
    const overrideValue = overrides[key];
    if (typeof overrideValue === "function") {
      merged[key] = overrideValue;
    } else if (
      overrideValue &&
      typeof overrideValue === "object" &&
      !Array.isArray(overrideValue)
    ) {
      merged[key] = { ...(base[key] || {}), ...overrideValue };
    } else {
      merged[key] = overrideValue;
    }
  });
  return merged;
};

const getStyles = (dark) => (dark ? mergeStyles(styles, darkOverrides) : styles);

const SSO_PROTOCOL_OPTIONS = [
  {
    value: "saml",
    label: "SAML 2.0",
    description: "XML assertions from a SAML IdP.",
  },
  {
    value: "oidc",
    label: "OIDC",
    description: "Authorization code login with Okta, Entra ID, or generic OIDC.",
  },
];

const OIDC_PROVIDER_OPTIONS = [
  { value: "generic", label: "Generic OIDC" },
  { value: "okta", label: "Okta" },
  { value: "azure", label: "Microsoft Entra ID" },
];

export default class SamlAuthentication extends React.Component {
  state = {
    ACTIVE_SSO_PROVIDER: "saml",
    SAML_ENABLED: false,
    SAML_ENTITY_ID: "",
    SAML_LOGIN_URL: "",
    SAML_CERTIFICATE: "",
    SAML_ACS_URL: "",
    SAML_SP_ENTITY_ID: "",
    OIDC_ENABLED: false,
    OIDC_PROVIDER_TYPE: "generic",
    OIDC_ISSUER_URL: "",
    OIDC_CLIENT_ID: "",
    OIDC_CLIENT_SECRET: "",
    OIDC_CLIENT_SECRET_SET: false,
    OIDC_SCOPES: "openid email profile",
    OIDC_EMAIL_CLAIM: "",
    OIDC_REQUIRE_VERIFIED_EMAIL: false,
    OIDC_LOGIN_URL: "",
    OIDC_REDIRECT_URI: "",
    enterprise_id: "",
    id: null,
    hasChanges: false,
    loading: false,
    dark: typeof document !== "undefined" && document.body.classList.contains("dark-mode"),
  };

  componentDidMount() {
    if (this.props.resources.userEnterpriseLoaded) {
      this.syncEnterpriseSettings(this.props.resources.userEnterprise);
    }
    this.setupDarkModeListener();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resources.userEnterprise !== this.props.resources.userEnterprise &&
      this.props.resources.userEnterprise
    ) {
      this.syncEnterpriseSettings(this.props.resources.userEnterprise);
    }
  }

  componentWillUnmount() {
    if (this.darkObserver) {
      this.darkObserver.disconnect();
    }
  }

  syncEnterpriseSettings = (enterprise = {}) => {
    this.setState((prev) => ({
      ACTIVE_SSO_PROVIDER: enterprise.OIDC_ENABLED ? "oidc" : "saml",
      SAML_ENABLED: !!enterprise.SAML_ENABLED,
      SAML_ENTITY_ID: enterprise.SAML_ENTITY_ID || "",
      SAML_LOGIN_URL: enterprise.SAML_LOGIN_URL || "",
      SAML_CERTIFICATE: enterprise.SAML_CERTIFICATE || "",
      SAML_ACS_URL: enterprise.SAML_ACS_URL || "",
      SAML_SP_ENTITY_ID: enterprise.SAML_SP_ENTITY_ID || "",
      OIDC_ENABLED: !!enterprise.OIDC_ENABLED,
      OIDC_PROVIDER_TYPE: enterprise.OIDC_PROVIDER_TYPE || "generic",
      OIDC_ISSUER_URL: enterprise.OIDC_ISSUER_URL || "",
      OIDC_CLIENT_ID: enterprise.OIDC_CLIENT_ID || "",
      OIDC_CLIENT_SECRET: "",
      OIDC_CLIENT_SECRET_SET: !!enterprise.OIDC_CLIENT_SECRET_SET,
      OIDC_SCOPES: enterprise.OIDC_SCOPES || "openid email profile",
      OIDC_EMAIL_CLAIM: enterprise.OIDC_EMAIL_CLAIM || "",
      OIDC_REQUIRE_VERIFIED_EMAIL: !!enterprise.OIDC_REQUIRE_VERIFIED_EMAIL,
      OIDC_LOGIN_URL: enterprise.OIDC_LOGIN_URL || "",
      OIDC_REDIRECT_URI: enterprise.OIDC_REDIRECT_URI || "",
      enterprise_id: enterprise.enterprise_id || enterprise.id || prev.enterprise_id,
      id: enterprise.id || prev.id,
      hasChanges: false,
      loading: false,
    }));
  };

  setupDarkModeListener = () => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return;
    }

    const isDark = document.body.classList.contains("dark-mode");
    if (this.state.dark !== isDark) {
      this.setState({ dark: isDark });
    }

    this.darkObserver = new MutationObserver(() => {
      const darkMode = document.body.classList.contains("dark-mode");
      this.setState((prev) => (prev.dark === darkMode ? null : { dark: darkMode }));
    });

    this.darkObserver.observe(document.body, { attributes: true, attributeFilter: ["class"] });
  };

  getThemeStyles = () => getStyles(this.state.dark);

  onCancel = () => {
    this.syncEnterpriseSettings(this.props.resources.userEnterprise || {});
  };

  onSave = () => {
    const {
      SAML_ENABLED,
      SAML_ENTITY_ID,
      SAML_LOGIN_URL,
      SAML_CERTIFICATE,
      OIDC_ENABLED,
      OIDC_PROVIDER_TYPE,
      OIDC_ISSUER_URL,
      OIDC_CLIENT_ID,
      OIDC_CLIENT_SECRET,
      OIDC_SCOPES,
      OIDC_EMAIL_CLAIM,
      OIDC_REQUIRE_VERIFIED_EMAIL,
      id,
      hasChanges,
      loading,
    } = this.state;
    if (loading || !hasChanges || !id) {
      return;
    }

    const postPackage = {
      SAML_ENABLED,
      SAML_ENTITY_ID,
      SAML_LOGIN_URL,
      SAML_CERTIFICATE,
      OIDC_ENABLED,
      OIDC_PROVIDER_TYPE,
      OIDC_ISSUER_URL,
      OIDC_CLIENT_ID,
      OIDC_SCOPES,
      OIDC_EMAIL_CLAIM,
      OIDC_REQUIRE_VERIFIED_EMAIL,
    };
    if (String(OIDC_CLIENT_SECRET || "").trim()) {
      postPackage.OIDC_CLIENT_SECRET = OIDC_CLIENT_SECRET;
    }

    this.setState({ loading: true });

    axios
      .put(`/v1/enterprises/${id}`, postPackage, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
      })
      .then((res) => {
        const updated = res?.data?.data;
        if (updated) {
          if (this.props.updateFetchWrapperState) {
            this.props.updateFetchWrapperState({
              userEnterprise: updated,
            });
          }
          this.syncEnterpriseSettings(updated);
        } else {
          this.setState({ loading: false, hasChanges: false });
        }
        NotificationManager.success("SSO settings saved successfully.");
      })
      .catch((err) => {
        console.log(err);
        this.setState({ loading: false });
        NotificationManager.alert("Error saving SSO settings.");
      });
  };

  handleCopy = (value, label) => {
    if (!value) {
      NotificationManager.alert(`No ${label} available to copy.`);
      return;
    }

    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(() => NotificationManager.success(`${label} copied to clipboard.`))
        .catch(() => this.copyFallback(value, label));
    } else {
      this.copyFallback(value, label);
    }
  };

  copyFallback = (value, label) => {
    if (typeof document === "undefined") {
      NotificationManager.alert(`Unable to copy ${label}.`);
      return;
    }
    try {
      const textArea = document.createElement("textarea");
      textArea.value = value;
      textArea.setAttribute("readonly", "");
      textArea.style.position = "absolute";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      NotificationManager.success(`${label} copied to clipboard.`);
    } catch (error) {
      NotificationManager.alert(`Unable to copy ${label}.`);
    }
  };

  handleInputFocus = (event) => {
    const dark = this.state.dark;
    event.target.style.borderColor = dark ? "#60a5fa" : "#3b82f6";
    event.target.style.boxShadow = dark
      ? "0 0 0 3px rgba(96,165,250,0.25)"
      : "0 0 0 3px rgba(59,130,246,0.18)";
  };

  handleInputBlur = (event) => {
    const dark = this.state.dark;
    event.target.style.borderColor = dark ? "rgba(148, 163, 184, 0.4)" : "#cbd5f5";
    event.target.style.boxShadow = "none";
  };

  renderToggle = (provider = this.state.ACTIVE_SSO_PROVIDER) => {
    const styles = this.getThemeStyles();
    const key = provider === "oidc" ? "OIDC_ENABLED" : "SAML_ENABLED";
    const label = provider === "oidc" ? "OIDC" : "SAML";
    const enabled = this.state[key];
    return (
      <button
        type="button"
        aria-pressed={enabled}
        onClick={() =>
          this.setState((prev) => ({
            [key]: !prev[key],
            ACTIVE_SSO_PROVIDER: provider,
            hasChanges: true,
          }))
        }
        style={styles.toggleButton(enabled)}
      >
        <span>{enabled ? `${label} Enabled` : `${label} Disabled`}</span>
        <span style={styles.toggleIndicator(enabled)}>
          <span style={styles.toggleKnob(enabled)} />
        </span>
      </button>
    );
  };

  renderProtocolSelector = () => {
    const styles = this.getThemeStyles();
    return (
      <div style={styles.protocolSelector}>
        {SSO_PROTOCOL_OPTIONS.map((option) => {
          const active = this.state.ACTIVE_SSO_PROVIDER === option.value;
          return (
            <button
              key={option.value}
              type="button"
              style={styles.protocolButton(active)}
              onClick={() =>
                this.setState({
                  ACTIVE_SSO_PROVIDER: option.value,
                })
              }
            >
              <span style={styles.protocolLabel}>{option.label}</span>
              <span style={styles.protocolDescription}>{option.description}</span>
            </button>
          );
        })}
      </div>
    );
  };

  renderReadOnlyField = (value, label, emptyText) => {
    const styles = this.getThemeStyles();
    const hasValue = !!value;
    return (
      <div style={styles.readOnlyField}>
        <div style={styles.codeValue}>{hasValue ? value : emptyText}</div>
        <button
          type="button"
          style={{
            ...styles.copyButton,
            ...(hasValue ? {} : styles.copyButtonDisabled),
          }}
          onClick={() => this.handleCopy(value, label)}
          disabled={!hasValue}
        >
          <i className="material-icons" style={styles.copyIcon}>
            content_copy
          </i>
          Copy
        </button>
      </div>
    );
  };

  getSuggestedAcsUrl = () => {
    if (this.state.SAML_ACS_URL) {
      return this.state.SAML_ACS_URL;
    }
    if (typeof window !== "undefined") {
      const enterpriseId = this.state.enterprise_id || this.state.id;
      if (enterpriseId) {
        return `${window.location.origin}/sso/saml/${enterpriseId}`;
      }
      return `${window.location.origin}/sso/saml/<enterprise_id>`;
    }
    return "";
  };

  getSuggestedEntityId = () => {
    if (this.state.SAML_SP_ENTITY_ID) {
      return this.state.SAML_SP_ENTITY_ID;
    }
    if (typeof window !== "undefined") {
      return window.location.origin;
    }
    return "";
  };

  getSuggestedOidcRedirectUri = () => {
    if (this.state.OIDC_REDIRECT_URI) {
      return this.state.OIDC_REDIRECT_URI;
    }
    if (typeof window !== "undefined") {
      const enterpriseId = this.state.enterprise_id || this.state.id;
      if (enterpriseId) {
        return `${window.location.origin}/sso/oidc/${enterpriseId}/callback`;
      }
      return `${window.location.origin}/sso/oidc/<enterprise_id>/callback`;
    }
    return "";
  };

  getSuggestedOidcLoginUrl = () => {
    if (this.state.OIDC_LOGIN_URL) {
      return this.state.OIDC_LOGIN_URL;
    }
    if (typeof window !== "undefined") {
      const enterpriseId = this.state.enterprise_id || this.state.id;
      if (enterpriseId) {
        return `${window.location.origin}/sso/oidc/${enterpriseId}`;
      }
      return `${window.location.origin}/sso/oidc/<enterprise_id>`;
    }
    return "";
  };

  render() {
    const { resources } = this.props;
    const { userEnterpriseLoading, userEnterpriseLoaded } = resources;

    if (userEnterpriseLoading && !userEnterpriseLoaded) {
      return <Loading black />;
    }

    const {
      ACTIVE_SSO_PROVIDER,
      SAML_ENABLED,
      SAML_ENTITY_ID,
      SAML_LOGIN_URL,
      SAML_CERTIFICATE,
      SAML_ACS_URL,
      SAML_SP_ENTITY_ID,
      OIDC_ENABLED,
      OIDC_PROVIDER_TYPE,
      OIDC_ISSUER_URL,
      OIDC_CLIENT_ID,
      OIDC_CLIENT_SECRET,
      OIDC_CLIENT_SECRET_SET,
      OIDC_SCOPES,
      OIDC_EMAIL_CLAIM,
      OIDC_REQUIRE_VERIFIED_EMAIL,
      hasChanges,
      loading,
    } = this.state;

    const enabledProviderLabels = [
      SAML_ENABLED ? "SAML" : null,
      OIDC_ENABLED ? "OIDC" : null,
    ].filter(Boolean);
    const anySsoEnabled = enabledProviderLabels.length > 0;
    const activeProviderLabel = ACTIVE_SSO_PROVIDER === "oidc" ? "OIDC" : "SAML";
    const statusHint = anySsoEnabled
      ? "Team members can sign in through your identity provider. Local passwords remain available unless Require SSO is enabled in Team Settings."
      : "Enterprise SSO is currently disabled. Team members continue using Synaccess credentials until you enable a provider.";

    const disableActions = loading || !hasChanges;

    const styles = this.getThemeStyles();

    return (
      <div className="grid-container">
        <div className="grid-x grid-padding-x grid-padding-y">
          <div className="cell large-12">
            <div style={styles.pageWrapper}>
              <section style={styles.headerCard}>
                <div style={styles.headerTopRow}>
                  <h2 style={styles.title}>Team Single Sign-On</h2>
                  <p style={styles.subtitle}>
                    Connect your identity provider so your team can sign in through SAML 2.0 or OpenID Connect.
                  </p>
                  <div style={styles.statusBadge(anySsoEnabled)}>
                    <i className="material-icons" style={styles.statusIcon}>
                      {anySsoEnabled ? "verified_user" : "lock"}
                    </i>
                    {anySsoEnabled
                      ? `${enabledProviderLabels.join(" + ")} enabled`
                      : "SSO is disabled"}
                  </div>
                  <p style={styles.statusHint}>{statusHint}</p>
                </div>
                <div style={styles.headerActions}>
                  <button
                    type="button"
                    className="button secondary"
                    style={{
                      ...styles.actionButton,
                      ...styles.secondaryButton,
                      ...(disableActions ? styles.disabledButton : {}),
                    }}
                    onClick={this.onCancel}
                    disabled={disableActions}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="button"
                    style={{
                      ...styles.actionButton,
                      ...styles.primaryButton,
                      ...(disableActions ? styles.disabledButton : {}),
                    }}
                    onClick={this.onSave}
                    disabled={disableActions}
                  >
                    {loading ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </section>

              <div style={styles.contentGrid}>
                <section style={styles.infoCard}>
                  <h3 style={styles.cardTitle}>
                    {ACTIVE_SSO_PROVIDER === "oidc"
                      ? "Getting started with OIDC SSO"
                      : "Getting started with SAML SSO"}
                  </h3>
                  <p style={styles.cardDescription}>
                    Choose the protocol your identity provider will use for enterprise SSO. You can keep SAML configured while preparing OIDC.
                  </p>

                  {this.renderProtocolSelector()}

                  {ACTIVE_SSO_PROVIDER === "oidc" ? (
                    <>
                      <div style={styles.guideBlock}>
                        <h4 style={styles.guideTitle}>Implementation checklist</h4>
                        <ol style={styles.stepList}>
                          <li>
                            Create a web OpenID Connect application in Okta, Entra ID, or another OIDC provider.
                          </li>
                          <li>
                            Set the redirect URI to <span style={styles.inlineCode}>{this.getSuggestedOidcRedirectUri()}</span>.
                          </li>
                          <li>
                            Copy the issuer URL, client ID, and client secret from your provider into the configuration card.
                          </li>
                          <li>
                            Use scopes <span style={styles.inlineCode}>openid email profile</span> unless your provider requires a narrower set.
                          </li>
                          <li>
                            Save your changes, enable OIDC, and assign users or groups to the application in your IdP.
                          </li>
                        </ol>
                      </div>

                      <div style={styles.guideBlock}>
                        <h4 style={styles.guideTitle}>Provider notes</h4>
                        <ol style={styles.stepList}>
                          <li>
                            Okta issuer URLs usually look like <span style={styles.inlineCode}>https://your-domain.okta.com/oauth2/default</span> or a custom authorization server.
                          </li>
                          <li>
                            Entra ID issuer URLs should use the v2 endpoint, for example <span style={styles.inlineCode}>https://login.microsoftonline.com/tenant-id/v2.0</span>.
                          </li>
                          <li>
                            Use the Synaccess login URL <span style={styles.inlineCode}>{this.getSuggestedOidcLoginUrl()}</span> to test SP-initiated login after saving.
                          </li>
                        </ol>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={styles.guideBlock}>
                        <h4 style={styles.guideTitle}>Implementation checklist</h4>
                        <ol style={styles.stepList}>
                          <li>
                            Create an application in your identity provider and designate SAML 2.0 as the sign-on method.
                          </li>
                          <li>
                            Set the Assertion Consumer Service (ACS) URL to <span style={styles.inlineCode}>{this.getSuggestedAcsUrl()}</span>.
                          </li>
                          <li>
                            Configure the Audience URI / Entity ID with <span style={styles.inlineCode}>{this.getSuggestedEntityId()}</span>.
                          </li>
                          <li>
                            Retrieve the IdP Issuer, Login URL, and X.509 certificate from your provider and add them to the configuration card.
                          </li>
                          <li>
                            Save your changes, enable SAML, and assign users to the application inside your IdP to grant access.
                          </li>
                        </ol>
                      </div>

                      <div style={styles.guideBlock}>
                        <h4 style={styles.guideTitle}>Okta configuration tips</h4>
                        <ol style={styles.stepList}>
                          <li>
                            In Okta, navigate to <strong>Applications &gt; Create App Integration</strong> and choose <strong>SAML 2.0</strong>.
                          </li>
                          <li>
                            Use <span style={styles.inlineCode}>{this.getSuggestedAcsUrl()}</span> as the <strong>Single sign on URL</strong> and set the <strong>Audience URI</strong> to <span style={styles.inlineCode}>{this.getSuggestedEntityId()}</span>.
                          </li>
                          <li>
                            Map the <strong>Name ID format</strong> to <span style={styles.inlineCode}>EmailAddress</span> with the <strong>Application username</strong> set to <span style={styles.inlineCode}>Email</span>.
                          </li>
                          <li>
                            After creating the app, open the <strong>Sign On</strong> tab to copy the issuer, Identity Provider Single Sign-On URL, and signing certificate into Synaccess Cloud.
                          </li>
                          <li>
                            Assign the application to your users or groups and verify their first login from the Team Members section.
                          </li>
                        </ol>
                      </div>
                    </>
                  )}
                </section>

                <section style={styles.settingsCard}>
                  <div>
                    <h3 style={styles.cardTitle}>{activeProviderLabel} configuration</h3>
                    <p style={styles.cardDescription}>
                      Enter the values supplied by your identity provider. Changes are applied to your organization when you save.
                    </p>
                  </div>

                  {ACTIVE_SSO_PROVIDER === "oidc" ? (
                    <>
                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>OIDC access</h4>
                          <p style={styles.fieldDescription}>
                            Enable or disable OIDC SSO for your team. When both SAML and OIDC are configured, email-initiated SSO prefers OIDC.
                          </p>
                        </div>
                        {this.renderToggle("oidc")}
                        <p style={styles.helperText}>
                          OIDC requires an issuer URL, client ID, and saved client secret before users can complete login.
                        </p>
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Provider</h4>
                          <p style={styles.fieldDescription}>
                            Select a preset for clearer administration. Discovery still uses the issuer URL.
                          </p>
                        </div>
                        <select
                          value={OIDC_PROVIDER_TYPE}
                          onChange={(event) =>
                            this.setState({ OIDC_PROVIDER_TYPE: event.target.value, hasChanges: true })
                          }
                          onFocus={this.handleInputFocus}
                          onBlur={this.handleInputBlur}
                          style={styles.selectInput}
                        >
                          {OIDC_PROVIDER_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Issuer URL</h4>
                          <p style={styles.fieldDescription}>
                            The OIDC issuer used for discovery. It must match the issuer in the ID token.
                          </p>
                        </div>
                        <input
                          type="text"
                          value={OIDC_ISSUER_URL}
                          onChange={(event) =>
                            this.setState({ OIDC_ISSUER_URL: event.target.value, hasChanges: true })
                          }
                          onFocus={this.handleInputFocus}
                          onBlur={this.handleInputBlur}
                          placeholder="https://idp.example.com/oauth2/default"
                          style={styles.input}
                        />
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Client ID</h4>
                          <p style={styles.fieldDescription}>
                            The public identifier for the OIDC application in your identity provider.
                          </p>
                        </div>
                        <input
                          type="text"
                          value={OIDC_CLIENT_ID}
                          onChange={(event) =>
                            this.setState({ OIDC_CLIENT_ID: event.target.value, hasChanges: true })
                          }
                          onFocus={this.handleInputFocus}
                          onBlur={this.handleInputBlur}
                          placeholder="0oa..."
                          style={styles.input}
                        />
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Client secret</h4>
                          <p style={styles.fieldDescription}>
                            Client secrets are stored write-only. Leave this blank to keep the current saved secret.
                          </p>
                        </div>
                        <input
                          type="password"
                          value={OIDC_CLIENT_SECRET}
                          onChange={(event) =>
                            this.setState({ OIDC_CLIENT_SECRET: event.target.value, hasChanges: true })
                          }
                          onFocus={this.handleInputFocus}
                          onBlur={this.handleInputBlur}
                          placeholder={
                            OIDC_CLIENT_SECRET_SET
                              ? "Client secret is saved. Enter a new value to rotate."
                              : "Paste client secret"
                          }
                          autoComplete="new-password"
                          style={styles.input}
                        />
                        <p style={styles.helperText}>
                          {OIDC_CLIENT_SECRET_SET && !OIDC_CLIENT_SECRET
                            ? "A client secret is already saved for this provider."
                            : "Save after entering a new secret to activate or rotate OIDC login."}
                        </p>
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Scopes</h4>
                          <p style={styles.fieldDescription}>
                            Space-separated scopes requested during authorization.
                          </p>
                        </div>
                        <input
                          type="text"
                          value={OIDC_SCOPES}
                          onChange={(event) =>
                            this.setState({ OIDC_SCOPES: event.target.value, hasChanges: true })
                          }
                          onFocus={this.handleInputFocus}
                          onBlur={this.handleInputBlur}
                          placeholder="openid email profile"
                          style={styles.input}
                        />
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Email claim</h4>
                          <p style={styles.fieldDescription}>
                            Optional claim path to use for the user email. Leave blank to use the standard email claim.
                          </p>
                        </div>
                        <input
                          type="text"
                          value={OIDC_EMAIL_CLAIM}
                          onChange={(event) =>
                            this.setState({ OIDC_EMAIL_CLAIM: event.target.value, hasChanges: true })
                          }
                          onFocus={this.handleInputFocus}
                          onBlur={this.handleInputBlur}
                          placeholder="email"
                          style={styles.input}
                        />
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Verified email requirement</h4>
                          <p style={styles.fieldDescription}>
                            Require the ID token email to be marked verified when the provider emits an email_verified claim.
                          </p>
                        </div>
                        <button
                          type="button"
                          aria-pressed={OIDC_REQUIRE_VERIFIED_EMAIL}
                          onClick={() =>
                            this.setState((prev) => ({
                              OIDC_REQUIRE_VERIFIED_EMAIL: !prev.OIDC_REQUIRE_VERIFIED_EMAIL,
                              hasChanges: true,
                            }))
                          }
                          style={styles.toggleButton(OIDC_REQUIRE_VERIFIED_EMAIL)}
                        >
                          <span>
                            {OIDC_REQUIRE_VERIFIED_EMAIL
                              ? "Verified email required"
                              : "Verified email not required"}
                          </span>
                          <span style={styles.toggleIndicator(OIDC_REQUIRE_VERIFIED_EMAIL)}>
                            <span style={styles.toggleKnob(OIDC_REQUIRE_VERIFIED_EMAIL)} />
                          </span>
                        </button>
                      </div>

                      <div style={styles.sectionDivider} />

                      <div style={styles.readOnlyGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>OIDC redirect URI</h4>
                          <p style={styles.fieldDescription}>
                            Add this as a web redirect URI in your identity provider application.
                          </p>
                        </div>
                        {this.renderReadOnlyField(
                          this.getSuggestedOidcRedirectUri(),
                          "OIDC redirect URI",
                          "Redirect URI will populate after the organization is provisioned."
                        )}
                      </div>

                      <div style={styles.readOnlyGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>SSO login URL</h4>
                          <p style={styles.fieldDescription}>
                            Use this URL to test SP-initiated OIDC login after saving.
                          </p>
                        </div>
                        {this.renderReadOnlyField(
                          this.getSuggestedOidcLoginUrl(),
                          "SSO login URL",
                          "Login URL will populate after the organization is provisioned."
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>SAML access</h4>
                          <p style={styles.fieldDescription}>
                            Enable or disable SAML SSO for your team. When enabled, users authenticate through your IdP and follow the policies you enforce there.
                          </p>
                        </div>
                        {this.renderToggle("saml")}
                        <p style={styles.helperText}>
                          SAML requires an IdP issuer, login URL, and signing certificate before users can complete login.
                        </p>
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Identity Provider Issuer</h4>
                          <p style={styles.fieldDescription}>
                            The unique identifier provided by your IdP. This value must match exactly.
                          </p>
                        </div>
                        <input
                          type="text"
                          value={SAML_ENTITY_ID}
                          onChange={(event) =>
                            this.setState({ SAML_ENTITY_ID: event.target.value, hasChanges: true })
                          }
                          onFocus={this.handleInputFocus}
                          onBlur={this.handleInputBlur}
                          placeholder="https://idp.example.com/app/issuer"
                          style={styles.input}
                        />
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>SAML SSO Login URL</h4>
                          <p style={styles.fieldDescription}>
                            The endpoint where Synaccess Cloud will redirect users to authenticate.
                          </p>
                        </div>
                        <input
                          type="text"
                          value={SAML_LOGIN_URL}
                          onChange={(event) =>
                            this.setState({ SAML_LOGIN_URL: event.target.value, hasChanges: true })
                          }
                          onFocus={this.handleInputFocus}
                          onBlur={this.handleInputBlur}
                          placeholder="https://idp.example.com/login"
                          style={styles.input}
                        />
                      </div>

                      <div style={styles.fieldGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>SAML certificate</h4>
                          <p style={styles.fieldDescription}>
                            Paste the full X.509 certificate used by your IdP to sign assertions.
                          </p>
                        </div>
                        <textarea
                          value={SAML_CERTIFICATE}
                          onChange={(event) =>
                            this.setState({ SAML_CERTIFICATE: event.target.value, hasChanges: true })
                          }
                          onFocus={this.handleInputFocus}
                          onBlur={this.handleInputBlur}
                          placeholder={"-----BEGIN CERTIFICATE-----"}
                          style={styles.textarea}
                          rows={SAML_CERTIFICATE ? 8 : 6}
                        />
                        <p style={styles.helperText}>
                          Ensure the certificate stays current. Expired certificates cause authentication to fail immediately.
                        </p>
                      </div>

                      <div style={styles.sectionDivider} />

                      <div style={styles.readOnlyGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Assertion Consumer Service (ACS) URL</h4>
                          <p style={styles.fieldDescription}>
                            Provide this URL to your IdP so that SAML assertions are posted back to Synaccess Cloud.
                          </p>
                        </div>
                        {this.renderReadOnlyField(
                          SAML_ACS_URL || this.getSuggestedAcsUrl(),
                          "ACS URL",
                          "ACS URL will populate after the organization is provisioned."
                        )}
                      </div>

                      <div style={styles.readOnlyGroup}>
                        <div style={styles.fieldHeading}>
                          <h4 style={styles.fieldTitle}>Service Provider Entity ID</h4>
                          <p style={styles.fieldDescription}>
                            Use this value as the Audience URI in your IdP configuration.
                          </p>
                        </div>
                        {this.renderReadOnlyField(
                          SAML_SP_ENTITY_ID || this.getSuggestedEntityId(),
                          "Entity ID",
                          "Entity ID will populate automatically once available."
                        )}
                      </div>
                    </>
                  )}
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
